/**
 * The URL is the scope of the end-of-life pages — `docs/adr/7003`, CLAUDE.md §3.4.
 *
 * bata-reqs issue 35 P5. Every lifecycle read takes a MARKET and a MONTH and refuses without
 * either (`GET /exit/lifecycle-transitions?country_key=&effective_month=`, and the same pair on
 * the withdrawals): a transition takes effect on the 1st of a calendar month (owner ruling Q3),
 * country-wide. So the link carries both:
 *
 *   `?market=`              the market — exit's own key (`../market-season.ts`), so a planner
 *                           arriving from another exit page keeps the market they chose
 *   `?month=YYYY-MM`        the month the moves take effect; the read is sent its 1st
 *   `?transitionRunId=`     one recommendation run of that month; absent → the month's newest,
 *                           the read's own rule, and the page names the run that answered
 *   `?withdrawalRunId=`     one withdrawal run of that month, the same way
 *   `?outletRunId=`         one outlet-exit recommendation run of that month (issue 35 P4, ADR 7028)
 *   `?outletTransferRunId=` one outlet transfer run of that month
 *   `?article=`             on the withdrawals page, the accepted move whose doors are open
 *
 * The phase register (bata-platform ADR 7031, `docs/adr/7005`) reads the same `?market=` and
 * `?month=` and no run: the phase in force for a month is not a run's output but the latest
 * decision effective by then, else the inference in force.
 *
 * **No default for the market or the month.** Not "next month", not "the newest month with a
 * run": a month is a date, and CLAUDE.md §3.4 rule 1 defaults no date. The only market chosen for
 * the planner is the single market they can see (ADR 9056), and the page says so.
 *
 * **The run keys are not `?runId=`.** The address bar carries every key from page to page, and
 * `?runId=` is the consolidation pages' run (`../scope.ts`): a transition run carried into the
 * lane book would be read there as a consolidation run. The withdrawal's lines ARE in the lane
 * book under the withdrawal run's id, so the drill to them writes `?runId=` explicitly.
 *
 * Pure: every function takes a search string, so the tests assert on strings, not on `window`.
 */

import { MARKET_PARAM } from '../market-season';

export { MARKET_PARAM };
export const MONTH_PARAM = 'month';
export const TRANSITION_RUN_PARAM = 'transitionRunId';
export const WITHDRAWAL_RUN_PARAM = 'withdrawalRunId';
export const OUTLET_RUN_PARAM = 'outletRunId';
export const OUTLET_TRANSFER_RUN_PARAM = 'outletTransferRunId';
export const ARTICLE_PARAM = 'article';

export { OUTLET_PATH, OUTLET_TRANSFERS_PATH, PHASES_PATH, TRANSITIONS_PATH, WITHDRAWALS_PATH } from './paths';

export interface LifecycleScope {
  readonly market: string | null;
  /** `YYYY-MM`, or `null` when absent or malformed. */
  readonly month: string | null;
  readonly transitionRunId: string | null;
  readonly withdrawalRunId: string | null;
  readonly outletRunId: string | null;
  readonly outletTransferRunId: string | null;
  readonly article: string | null;
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function read(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim() ?? '';
  return value === '' ? null : value;
}

/** The scope a search string states. Pure: no `window`, no default, no guess. */
export function readLifecycleScope(search: string): LifecycleScope {
  const params = new URLSearchParams(search);
  const month = read(params, MONTH_PARAM);
  return {
    market: read(params, MARKET_PARAM),
    // A malformed month is no month: a read sent `2026-13-01` would spend a 422 on a typo.
    month: month !== null && MONTH.test(month) ? month : null,
    transitionRunId: read(params, TRANSITION_RUN_PARAM),
    withdrawalRunId: read(params, WITHDRAWAL_RUN_PARAM),
    outletRunId: read(params, OUTLET_RUN_PARAM),
    outletTransferRunId: read(params, OUTLET_TRANSFER_RUN_PARAM),
    article: read(params, ARTICLE_PARAM),
  };
}

/** `YYYY-MM` → the 1st, `YYYY-MM-01`, as the reads take it. Pure. */
export function firstOfMonth(month: string): string {
  return `${month}-01`;
}

/** An ISO date → its month, `YYYY-MM`. Pure. */
export function monthOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** `search` with each key set, or removed for `null`, every other key kept. Pure. */
export function withLifecycleScope(search: string, values: Readonly<Record<string, string | null>>): string {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(values)) {
    if (value === null || value.trim() === '') params.delete(key);
    else params.set(key, value);
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * The query of a read bounded by market and month, or `null` while either is missing — the
 * caller then issues no request (§3.4 rule 2).
 */
export function monthQuery(
  scope: LifecycleScope,
  runId: string | null,
): { readonly country_key: string; readonly effective_month: string; readonly run_id?: string } | null {
  if (scope.market === null || scope.month === null) return null;
  return {
    country_key: scope.market,
    effective_month: firstOfMonth(scope.month),
    ...(runId === null ? {} : { run_id: runId }),
  };
}

/**
 * The phase register's query — market and the month's 1st, as `GET /exit/lifecycle-phases` and its
 * summary take them — or `null` while either is missing: no request is issued (§3.4 rule 2).
 */
export function registerQuery(scope: LifecycleScope): { readonly country_key: string; readonly month: string } | null {
  if (scope.market === null || scope.month === null) return null;
  return { country_key: scope.market, month: firstOfMonth(scope.month) };
}

/** The article is the path's last segment: `/exit/lifecycle-phases/<article>`. Pure. */
export function phaseArticleOf(path: string): string | null {
  const match = /^\/exit\/lifecycle-phases\/([^/?#]+)/.exec(path);
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

/**
 * The months the register's chooser offers: from two years before `now` to six months after it,
 * newest first, with the linked one kept. A list of choices, never a choice: nothing is selected
 * until the planner picks a month (CLAUDE.md §3.4 rule 1). `now` is an argument so tests fix it. Pure.
 */
export function registerMonths(now: number, linked: string | null): readonly string[] {
  const today = new Date(now);
  const months = new Set<string>();
  for (let offset = 6; offset >= -24; offset -= 1) {
    const at = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + offset, 1));
    months.add(at.toISOString().slice(0, 7));
  }
  if (linked !== null) months.add(linked);
  return [...months].sort().reverse();
}
